import React, { useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { func, array } from 'prop-types';
import { object, string } from 'yup';
import { Box, Flex } from 'rebass';
import { ButtonStyled, DropDownStyled, TextFieldStyled, TextStyled } from '@nike/nike-design-system-components';
import { Formik } from 'formik';
import join from 'url-join';

import { nike } from '@nike/nike-design-tokens';
import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import { fetchSeasons, findProducts, saveFindProductFormState } from '../actions';

import { getSeasons, getFindProductForm } from '../selectors';

import { isValidProductCode } from '../utils';

import { showToast } from '../../../components/toast/actions';
import TableServerSide from '../../../components/tableServerSide/TableServerSide';

import { getFindProductsColumns } from './columns';

import { SALES_ORGS, SALES_ORG_TO_REGION } from '../constants';
import { ActionItemList } from '../../common/ActionItemList';

import useModal from '../../../hooks/useModal';

import http from '../../../util/http';

const validationSchema = object().shape({
  season: string().nullable().required('Please enter a season'),
  salesOrg: string().nullable().required('Please enter a sales org'),
  productCode: string()
    .nullable()
    .required('Please enter a list of product codes')
    .test('isProductCodeValid', 'Product code invalid', value => (value ? isValidProductCode(value) : null))
});

const formInitialValues = {
  season: null,
  salesOrg: null,
  productCode: null
};
const api2 = import.meta.env.VITE_API2_URL;

const getFilterQueryParams = values => {
  if (Object.keys(values).length <= 0) {
    return '';
  }

  const { season, salesOrg, productCode } = values;
  const filterString = `${season ? `&filter=seasonsIncludes(${season})` : ''}${
    salesOrg ? `&filter=salesOrg(${salesOrg})` : ''
  }${productCode ? `&filter=productCode(${productCode})` : ''}`;

  return filterString;
};

const FindProducts = ({ fetchSeasons, seasons, showToast, saveFindProductFormState, savedFindProductFormState }) => {
  useEffect(() => {
    fetchSeasons();
  }, []);

  const {
    tableInstance: { selectedRowIds = {}, selectedFlatRows },
    totalResources,
    refreshTableData,
    handleClearSortAndFilters
  } = useContext(TableServerSideContext);

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();

  const memoizedColumns = useMemo(() => getFindProductsColumns(), []);

  const [filters, setFilters] = useState(getFilterQueryParams(savedFindProductFormState));

  const [selectedCodeIsInvalid, setSelectedCodeIsInvalid] = useState(false);
  const [updatedWithProductCode, setUpdatedWithProductCode] = useState('');

  const seasonOptions = seasons.map(season => ({ label: season.name, value: season.name }));

  const { podiumCdsColorGrey500 } = nike;

  useEffect(() => {
    refreshTableData();
  }, [filters]);

  const onSubmit = (values, { setSubmitting }) => {
    const filterQueryParams = getFilterQueryParams(values);
    setFilters(filterQueryParams);
    setSubmitting(false);

    // persist form state when navigating
    saveFindProductFormState(values);
  };

  const bulkDeleteProducts = payload => {
    const url = join(api2, 'v1/catalogProducts/bulkDeleteProducts');

    return http.post(url, payload).catch(() => {
      showToast({ message: 'Cannot delete Product Codes', type: 'error' }, 5500);
    });
  };

  const replaceProductCode = payload => {
    const url = join(api2, 'v1/catalogProducts/bulkUpdateProducts');

    return http.post(url, payload).catch(() => {
      showToast({ message: 'Cannot replace Product Codes', type: 'error' }, 5500);
    });
  };

  const selectedCatalogIds = selectedFlatRows?.map(row => row.original.catalogId) || [];
  const selectedSalesOrgs = selectedFlatRows?.map(row => row.original.salesOrg) || [];
  const selectedProductCodes = selectedFlatRows?.map(row => row.original.productCode) || [];
  const selectedSeasons = (selectedFlatRows?.map(row => row.original.seasons) || []).flat();

  const closeModal = props => {
    closeActionItemModal(props);
    setSelectedCodeIsInvalid(false);
  };

  const actionItems = {
    Delete: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Delete',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Deleting Product Codes...');

                const catalogIds = selectedFlatRows?.map(row => row.original.catalogId) || [];
                const salesOrgs = selectedFlatRows?.map(row => row.original.salesOrg) || [];
                const productCodes = selectedFlatRows?.map(row => row.original.productCode) || [];
                const seasons = (selectedFlatRows?.map(row => row.original.seasons) || []).flat();

                await bulkDeleteProducts({
                  catalogIds,
                  salesOrgs: [...new Set(salesOrgs)],
                  productCodes: [...new Set(productCodes)],
                  seasons: [...new Set(seasons)]
                });

                refreshTableData();

                toggleIsLoading(false);

                closeActionItemModal();

                showToast({ message: 'Product Codes deleted', type: 'success' }, 5500);
              }
          }
        ],
        content: () => 'Do you want to proceed with deleting the selected Product Codes?',
        title: 'Confirm Deletion'
      }
    },
    Replace: {
      modal: {
        actions: [
          {
            actionName: 'Cancel',
            action: closeModal
          },
          {
            actionName: 'Replace',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                setSelectedCodeIsInvalid(false);
                toggleIsLoading(true, 'Replacing Product Codes...');

                const objects = await findProducts({
                  productCodes: [updatedWithProductCode],
                  seasonCodes: selectedSeasons,
                  regionCodes: [SALES_ORG_TO_REGION[selectedSalesOrgs]]
                });

                if (objects.length) {
                  await replaceProductCode({
                    productCodes: selectedProductCodes,
                    catalogIds: selectedCatalogIds,
                    salesOrgs: selectedSalesOrgs,
                    seasons: selectedSeasons,
                    updatedWith: updatedWithProductCode
                  });

                  refreshTableData();

                  closeActionItemModal();

                  showToast({ message: 'Product Codes replaced', type: 'success' }, 5500);
                } else setSelectedCodeIsInvalid(true);

                toggleIsLoading(false);
              },
            disabled: !isValidProductCode(updatedWithProductCode)
          }
        ],
        content: () => (
          <>
            <TextFieldStyled
              label='Replace with'
              name='findProductsReplaceWith'
              id='findProductsReplaceWith'
              onChange={e => setUpdatedWithProductCode(e.target.value)}
            />
            {selectedCodeIsInvalid && (
              <TextStyled appearance='body2' color='red' style={{ paddingLeft: '5px', marginBottom: '10px' }}>
                Replacement product code invalid for season and/or sales org of selected catalog(s).
              </TextStyled>
            )}
          </>
        ),
        title: 'Replace Selected'
      }
    }
  };

  return (
    <Box height='100%'>
      <Formik
        initialValues={
          Object.keys(savedFindProductFormState).length > 0 ? savedFindProductFormState : formInitialValues
        }
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ values, handleChange, handleSubmit, isValid, dirty, resetForm }) => {
          const isFindAllButtonDisabled = !isValid || !dirty;

          const handleClearAll = () => {
            resetForm();
            setFilters('');
            saveFindProductFormState({});
          };

          return (
            <Box mt='10px' height='100%' border={podiumCdsColorGrey500}>
              <form onSubmit={handleSubmit}>
                <Flex flexDirection='row' width='100%' alignItems='center'>
                  <Box width='275px'>
                    <DropDownStyled
                      id='season'
                      name='season'
                      label='Select Season'
                      options={seasonOptions}
                      value={values.season}
                      onChange={handleChange}
                    />
                  </Box>
                  <Box ml='24px' width='275px'>
                    <DropDownStyled
                      id='salesOrg'
                      name='salesOrg'
                      label='Select Sales Org'
                      options={SALES_ORGS}
                      value={values.salesOrg}
                      onChange={handleChange}
                    />
                  </Box>
                  <Box ml='24px' width='400px'>
                    <TextFieldStyled
                      id='productCode'
                      name='productCode'
                      label='Find Product Code'
                      value={values.productCode}
                      onChange={handleChange}
                    />
                  </Box>
                  <Box ml='24px' mb='7px'>
                    <ButtonStyled type='button' appearance='secondary' background='light' onClick={handleClearAll}>
                      Clear All
                    </ButtonStyled>
                  </Box>
                  <Box ml='24px' mb='7px'>
                    <ButtonStyled type='submit' disabled={isFindAllButtonDisabled}>
                      Find All
                    </ButtonStyled>
                  </Box>
                </Flex>
              </form>

              {filters ? (
                <Box>
                  <Flex alignItems='end'>
                    <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
                      {totalResources?.toLocaleString('en-US')} product{totalResources !== 1 && 's'}
                    </TextStyled>

                    <Flex>
                      <ButtonStyled
                        appearance='text'
                        background='light'
                        ariaLabel='Clear All Filters'
                        type='button'
                        onClick={handleClearSortAndFilters}
                        style={{ marginRight: '10px' }}
                      >
                        Clear All Filters
                      </ButtonStyled>
                    </Flex>

                    <ActionItemList
                      selected={selectedRowIds}
                      actionItems={actionItems}
                      isModalOpen={isActionItemModalOpen}
                      openModal={openActionItemModal}
                      closeModal={closeModal}
                    />
                  </Flex>

                  <TableServerSide
                    columns={memoizedColumns}
                    includeRowSelect
                    url='v1/catalogProducts'
                    extraParams={filters}
                    fullWidth
                    fullHeight
                  />
                </Box>
              ) : (
                <Flex
                  alignItems='center'
                  justifyContent='center'
                  mt='200px'
                  width='100%'
                  border={`1px solid ${podiumCdsColorGrey500}`}
                >
                  <TextStyled appearance='body1' color={podiumCdsColorGrey500}>
                    Please enter a season, sales org, and product code
                  </TextStyled>
                </Flex>
              )}
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};

FindProducts.propTypes = {
  seasons: array.isRequired,
  fetchSeasons: func.isRequired,
  showToast: func.isRequired,
  saveFindProductFormState: func.isRequired,
  savedFindProductFormState: object.isRequired
};

const mapStateToProps = state => {
  return {
    seasons: getSeasons(state),
    savedFindProductFormState: getFindProductForm(state)
  };
};

export default connect(mapStateToProps, { fetchSeasons, showToast, saveFindProductFormState })(FindProducts);
